import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'
import type { ProofType } from '../types/challenge'

export interface SubmitProofParams {
  proofType: ProofType
  round: number
  file?: File
  text?: string
}

export function parseSubmitError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message
    if (msg.includes('user rejected') || msg.includes('ACTION_REJECTED'))
      return '트랜잭션을 거부했습니다'
    if (msg.includes('already submitted')) return '이미 이 라운드에 인증을 제출했습니다'
    if (msg.includes('deadline') || msg.includes('Deadline')) return '인증 마감 시각이 지났습니다'
    if (msg.includes('not participant')) return '챌린지 참가자가 아닙니다'
    if (msg.startsWith('Upload failed:')) return msg.slice('Upload failed: '.length)
    return msg.slice(0, 120)
  }
  return '인증 제출에 실패했습니다'
}

type SubmitFn = (cidHash: string, round: number) => Promise<ethers.TransactionResponse>

export function useSubmitProof(challengeAddress: string) {
  const queryClient = useQueryClient()

  return useMutation<string, Error, SubmitProofParams>({
    mutationFn: async ({ proofType, round, file, text }) => {
      if (!window.ethereum) throw new Error('MetaMask가 설치되어 있지 않습니다')

      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

      // Step 1: derive the on-chain commitment (cidHash) and store proof content off-chain
      // according to the challenge's proof type. The contract just records the hash.
      let cidHash: string

      if (proofType === 'photo') {
        if (!file) throw new Error('사진을 선택해주세요')
        const fileBytes = new Uint8Array(await file.arrayBuffer())
        cidHash = ethers.keccak256(fileBytes)
        const formData = new FormData()
        formData.append('file', file)
        formData.append('cidHash', cidHash)
        const resp = await fetch(`${apiBase}/api/upload/image`, { method: 'POST', body: formData })
        if (!resp.ok) throw new Error('Upload failed: 이미지 업로드에 실패했습니다')
      } else if (proofType === 'text') {
        const t = (text ?? '').trim()
        if (!t) throw new Error('인증 내용을 입력해주세요')
        cidHash = ethers.keccak256(ethers.toUtf8Bytes(t))
        const resp = await fetch(`${apiBase}/api/proof/${cidHash}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: t }),
        })
        if (!resp.ok) throw new Error('Upload failed: 인증 내용 저장에 실패했습니다')
      } else {
        // checkin: a non-zero marker hash; nothing stored off-chain (the on-chain record is the proof)
        cidHash = ethers.keccak256(
          ethers.toUtf8Bytes(`checkin:${challengeAddress}:${round}:${Date.now()}`),
        )
      }

      // Step 2: Submit proof on-chain with the commitment hash
      const browserProvider = new ethers.BrowserProvider(window.ethereum)
      const signer = await browserProvider.getSigner()
      const contract = new ethers.Contract(challengeAddress, CHALLENGE_ABI, signer)

      const tx = await (contract.submitProof as SubmitFn)(cidHash, round)
      await tx.wait()

      return cidHash
    },
    onSuccess: (_cidHash, { round }) => {
      void queryClient.invalidateQueries({ queryKey: ['proofs', challengeAddress, round] })
      void queryClient.invalidateQueries({ queryKey: ['challenge-detail', challengeAddress] })
    },
  })
}
